import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Heading,
  MaterialIcons,
  CircularProgress,
  Button,
  MaterialUICore,
  Body,
} from '@iclinic/design-system';

import * as S from './styles';
import {
  getCategories,
  isFetchingCategories,
  isCategoriesOffCanvasOpen,
} from 'features/free-samples/state/opt-in-confirmation/selectors';
import {
  closeOptinCategoriesOffCanvas,
  fetchCategoriesData,
  goBackToPharmasModal,
  openSuccessModal,
  sendIntercomEvent,
  setOptInConfirmation,
  setSelectedCategories,
} from 'features/free-samples/state/opt-in-confirmation';
import { PharmaceuticalCategory } from 'features/free-samples/services/types';

const { Close } = MaterialIcons;
const { List, ListItemText } = MaterialUICore;

const CategoryOffCanvas = () => {
  const isOpen = useSelector(isCategoriesOffCanvasOpen);

  const dispatch = useDispatch();
  const isFetching = useSelector(isFetchingCategories);
  const categories = useSelector(getCategories);

  useEffect(() => {
    dispatch(fetchCategoriesData());
  }, [dispatch]);

  const handleCloseCategoriesOffCanvas = () => {
    dispatch(closeOptinCategoriesOffCanvas());
  };

  const handleBack = () => {
    dispatch(goBackToPharmasModal());
  };

  const [checkedCategories, setCheckedCategories] = useState<
    PharmaceuticalCategory[]
  >([]);

  useEffect(() => {
    setCheckedCategories(categories);
  }, [categories]);

  const onPhysicianClick = (category: PharmaceuticalCategory) => {
    setCheckedCategories((previousState) => {
      const hasCategories = previousState.find(
        (item) => item.id === category.id,
      );

      if (hasCategories) {
        return previousState.filter((item) => item.id !== category.id);
      }

      return [...previousState, category];
    });
  };

  const onSelectAllPhysiciansClick = () => {
    setCheckedCategories((previous) => {
      if (previous.length === categories.length) {
        return [];
      }

      return categories;
    });
  };

  const onSubmitSelections = () => {
    dispatch(setSelectedCategories(checkedCategories));
    dispatch(setOptInConfirmation());
    dispatch(sendIntercomEvent('optin_form_completed'));
    dispatch(openSuccessModal());
  };

  return (
    <S.DrawerOfCanvas
      open={isOpen}
      onClose={handleCloseCategoriesOffCanvas}
      anchor="right"
      elevation={0}
      data-testid="categories-offcanvas"
    >
      <S.ContentOffCanvas>
        <S.HeaderOffCanvas>
          <Heading variant="sm" gutterBottom>
            Selecione as categorias
          </Heading>

          <S.CloseIconButton
            aria-label="Botão fechar"
            size="small"
            onClick={handleCloseCategoriesOffCanvas}
          >
            <Close />
          </S.CloseIconButton>
        </S.HeaderOffCanvas>

        <S.DescriptionOffCanvas variant="xs">
          Selecione as categorias de medicamentos que você tem interesse em
          receber
        </S.DescriptionOffCanvas>

        <S.ChipContainer>
          {isFetching ? (
            <CircularProgress />
          ) : (
            <S.SelectCategoriesWrapper>
              <S.ListWrapperCategories>
                <List>
                  <S.ListItem button onClick={onSelectAllPhysiciansClick} dense>
                    <S.ListItemIcon>
                      <S.CheckboxListItem
                        edge="start"
                        size="medium"
                        color="primary"
                        disableRipple
                        checked={checkedCategories.length === categories.length}
                      />
                    </S.ListItemIcon>

                    <ListItemText
                      primary={
                        <Body variant="xxs">
                          Selecionar todas as categorias
                        </Body>
                      }
                    />
                  </S.ListItem>

                  {categories.map((category) => (
                    <S.ListItem
                      button
                      key={category.name}
                      data-testid={`category-checkbox-${category.id}`}
                      onClick={() => onPhysicianClick(category)}
                      dense
                    >
                      <S.ListItemIcon>
                        <S.CheckboxListItem
                          edge="start"
                          size="medium"
                          color="primary"
                          disableRipple
                          checked={
                            !!checkedCategories.find(
                              (item) => item.id === category.id,
                            )
                          }
                        />
                      </S.ListItemIcon>

                      <ListItemText
                        primary={<Body variant="xxs">{category.name}</Body>}
                      />
                    </S.ListItem>
                  ))}
                </List>
              </S.ListWrapperCategories>
            </S.SelectCategoriesWrapper>
          )}
        </S.ChipContainer>

        <S.FooterWrapperCanvas>
          <Button variant="outlined" onClick={handleBack}>
            Voltar
          </Button>

          <Button
            color="primary"
            onClick={onSubmitSelections}
            disabled={!checkedCategories.length}
          >
            Enviar
          </Button>
        </S.FooterWrapperCanvas>
      </S.ContentOffCanvas>
    </S.DrawerOfCanvas>
  );
};

export default CategoryOffCanvas;
